import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { useCallback, useMemo } from "react";

import { useTabCountQuery } from "../../../api/repApi";
import { useRepOnboardingAuditApi } from "../../../api/repOnboardingAuditApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useRepContext, useRepOnboardingAuditContext } from "../../../contexts/editableItemContext";
import { useUserContext } from "../../../contexts/userContext";
import { buildSaveChain } from "../../../helpers/saveChain";
import { RepOnboardingAudit } from "../../../models";
import { tabs } from "../../../string";
import { useBGCLists } from "../../bgChecks/editor";
import { useDrugTestLists } from "../../drugTests/editor";

interface IState extends ITemplateData {
	repOnboardingAudit: RepOnboardingAudit;
}

export function useRepOnboardingAudits() {
	const [bgcLists, bgcListsLoaded] = useBGCLists();
	const [drugTestLists, drugTestListsLoaded] = useDrugTestLists();

	const activeLists = useMemo(() => {
		return {
			screeningProviders: bgcLists?.screeningProviders,
			types: drugTestLists.types,
		};
	}, [bgcListsLoaded, drugTestListsLoaded]);

	return [activeLists];
}

function OnboardingAuditEditorDynamic() {
	const { repId } = useRepContext();
	const { user } = useUserContext();
	const { rep } = useRepContext();
	const context = useRepOnboardingAuditContext();
	const [activeLists] = useRepOnboardingAudits();
	const { saveRepOnboardingAudit, loadRepOnboardingAudit, useRepOnboardingAuditsQuery } = useRepOnboardingAuditApi();
	const { clearQuery } = useRepOnboardingAuditsQuery(repId);
	const repOnboardingAudit = context.item;
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);
	const templateData = useMemo(
		() => ({
			repOnboardingAudit,
			user,
			rep,
			activeLists,
		}),
		[repOnboardingAudit, user, rep, activeLists],
	);

	const save = useCallback(
		() =>
			buildSaveChain(saveRepOnboardingAudit, "Saving Onboarding Audit")
				.then(loadRepOnboardingAudit, "Loading...")
				.thenRun(clearQuery)
				.thenRun(clearTabCount)
				.execute(repOnboardingAudit),
		[repOnboardingAudit, saveRepOnboardingAudit, loadRepOnboardingAudit],
	);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function updateValueInternal(id: string, value: any, currentState: IState, parentComponent?: string) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return newState ?? currentState;
	}

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}

		let setBaseProperty = true;
		let translateBaseValue = false;
		//Custom logic
		switch (id) {
			case "repOnboardingAudit.backgroundCheck.investigationId":
			case "repOnboardingAudit.drugTest.specimenId":
				setBaseProperty = false;
				break;
			case "repOnboardingAudit.backgroundCheck.testCompanyId":
			case "repOnboardingAudit.drugTest.typeId":
				translateBaseValue = true;
				break;
			case "repOnboardingAudit.backgroundCheck.testCompanyName":
				if (value !== "Other") await update("repOnboardingAudit.backgroundCheck.testCompanyOther", null);
				break;
			default:
				break;
		}

		if (id.endsWith("Id") && setBaseProperty) {
			const baseName = id.substring(0, id.length - 2);
			const baseValue = translateBaseValue ? value : value == null ? null : value === 1;
			await update(baseName, baseValue);
		}

		return newState as IState;
	}

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.onboardingAudits}
			otherTemplateData={templateData}
			itemProp="repOnboardingAudit"
			updateValues={updateValues}
			save={save}
			loaded
		/>
	);
}

export default OnboardingAuditEditorDynamic;
